import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Icon } from "../icon/Icon";

type Size = "medium" | "small";

const SIZES: Record<Size, { font: string; lh: string; icon: number }> = {
  medium: { font: "var(--font-size-body-2)", lh: "var(--line-height-body-2)", icon: 16 },
  small: { font: "var(--font-size-label-1)", lh: "var(--line-height-label-1)", icon: 14 },
};

export interface TextButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  color?: "primary" | "assistive" | "normal";
  size?: Size;
  chevron?: boolean;
  underline?: boolean;
  disabled?: boolean;
  children?: ReactNode;
}

export function TextButton({
  children,
  color = "primary",
  size = "medium",
  disabled = false,
  chevron = false,
  underline = false,
  style,
  ...rest
}: TextButtonProps) {
  const s = SIZES[size] || SIZES.medium;
  const c = disabled
    ? "var(--label-disable)"
    : color === "primary"
      ? "var(--primary-normal)"
      : color === "assistive"
        ? "var(--label-alternative)"
        : "var(--label-normal)";
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        padding: 0,
        border: "none",
        background: "transparent",
        color: c,
        fontFamily: "var(--font-sans)",
        fontSize: s.font,
        lineHeight: s.lh,
        fontWeight: "var(--font-weight-semibold)",
        textDecoration: underline ? "underline" : "none",
        textUnderlineOffset: 2,
        cursor: disabled ? "not-allowed" : "pointer",
        WebkitTapHighlightColor: "transparent",
        ...style,
      }}
      {...rest}
    >
      {children}
      {chevron && <Icon name="chevronRight" size={s.icon} />}
    </button>
  );
}

export default TextButton;
